<template>
  <div>
    <app-input
      v-if="showAdminRoleSelector"
      v-model="adminRole"
      type="select"
      name="Admin role"
      :label="$t('admins.add.roleLabel')"
      :options="adminRoleOptions"
      :description="adminRoleDescription"
    ></app-input>

    <!-- Tesco Admin Form (when tescoPortalStubsEnabled) -->
    <template v-if="tescoPortalStubsEnabled">
      <ValidationObserver v-slot="v">
        <b-form @submit.prevent="v.handleSubmit(onSave)">
          <!-- Standard User Fields -->
          <app-input
            v-model="admin.username"
            v-if="!shouldHideUsername"
            :rules="{ required: true, regex: /[a-zA-Z][a-zA-Z0-9_]*$/ }"
            :extraState="!errorDuplicated.username"
            type="text"
            name="username"
            :label="$t('admins.add.usernameLabel')"
            @input="onDuplicateInput('username')"
          >
            <b-form-invalid-feedback v-if="errorDuplicated.username">{{
              $t('admins.add.usernameTakenError')
            }}</b-form-invalid-feedback>
          </app-input>

          <app-input
            v-model="admin.email"
            rules="required|email"
            :extraState="!errorDuplicated.email"
            :validate-immediate="false"
            type="text"
            name="email"
            :label="$t('admins.add.emailLabel')"
            @input="onDuplicateInput('email')"
          >
            <b-form-invalid-feedback v-if="errorDuplicated.email">{{
              $t('admins.add.emailTakenError')
            }}</b-form-invalid-feedback>
          </app-input>

          <app-input
            v-model="admin.phone"
            rules="required"
            :extraState="!errorDuplicated.phone"
            :validate-immediate="false"
            type="text"
            name="phone"
            :label="$t('admins.add.phoneLabel')"
            @input="onDuplicateInput('phone')"
          >
            <b-form-invalid-feedback v-if="errorDuplicated.phone">{{
              $t('admins.add.phoneTakenError')
            }}</b-form-invalid-feedback>
          </app-input>

          <app-input
            vid="password"
            v-model="admin.password"
            :validate-immediate="false"
            rules="required|password"
            type="password"
            name="password"
            :label="$t('admins.add.passwordLabel')"
            :description="$t('admins.add.passwordDescription')"
            autocomplete="new-password"
          ></app-input>

          <app-input
            v-model="admin.confirmed"
            :validate-immediate="false"
            rules="required|password|confirmed:password"
            data-vv-as="password"
            type="password"
            name="confirm"
            :label="$t('admins.add.confirmPasswordLabel')"
            autocomplete="new-password"
          ></app-input>

          <!-- Admin Type Selector -->
          <app-input
            v-model="tescoAdminType"
            rules="required"
            :validate-immediate="false"
            type="select"
            name="Admin type"
            :label="$t('admins.add.adminTypeLabel')"
            :options="adminTypeOptions"
          ></app-input>

          <!-- Conditional sections for "Admin" type -->
          <template v-if="tescoAdminType === 'admin'">
            <!-- Portal Functionality Section -->
            <div class="mt-4">
              <h6>{{ $t('admins.add.portalFunctionalityTitle') }}</h6>
              <p class="text-muted">{{ $t('admins.add.portalFunctionalityDescription') }}</p>

              <b-form-group>
                <!-- Individual checkboxes for portal functionality -->
                <b-form-checkbox
                  v-for="option in portalFunctionalityOptionsBeforeEmployees"
                  :key="option.value"
                  v-model="portalFunctionality"
                  :value="option.value"
                  class="mb-1"
                  stacked
                >
                  {{ option.text }}
                </b-form-checkbox>

                <!-- Employees checkbox -->
                <b-form-checkbox v-model="portalFunctionality" value="employees" stacked>
                  {{ $t('nav.employees') }}
                </b-form-checkbox>

                <!-- Nested Employees Options - appears right after Employees checkbox -->
                <div v-if="portalFunctionality.includes('employees')" class="ml-4 mt-2 mb-3">
                  <!-- Division Checkboxes (only if divisions exist) -->
                  <div v-if="hasDivisions" class="mb-3">
                    <label class="d-block mb-2">{{ $t('admins.add.divisionsLabel') }}</label>
                    <app-input
                      v-model="selectedDivisions"
                      type="checkbox"
                      name="divisions"
                      :options="divisionOptions"
                      :disabled="portalFunctionality.includes('divisions')"
                      stacked
                    ></app-input>
                  </div>

                  <!-- Reporting Level Radio Options -->
                  <div>
                    <label class="d-block mb-2">{{ $t('admins.add.reportingLevelLabel') }}</label>
                    <app-input
                      v-model="reportingLevel"
                      type="radio"
                      name="reportingLevel"
                      :options="reportingLevelOptions"
                      stacked
                    ></app-input>
                  </div>
                </div>

                <!-- Remaining checkboxes after Employees -->
                <b-form-checkbox
                  v-for="option in portalFunctionalityOptionsAfterEmployees"
                  :key="option.value"
                  v-model="portalFunctionality"
                  :value="option.value"
                  stacked
                  class="mb-1"
                >
                  {{ option.text }}
                </b-form-checkbox>
              </b-form-group>
            </div>

            <!-- Employee Data Attributes Section -->
            <div v-if="shouldShowEmployeeDataAttributes" class="mt-4">
              <h6>{{ $t('admins.add.employeeDataAttributesTitle') }}</h6>
              <p class="text-muted">
                {{ $t('admins.add.employeeDataAttributesDescription') }}
              </p>

              <app-input
                v-model="employeeDataAttributes"
                type="checkbox"
                name="employeeDataAttributes"
                :options="employeeDataAttributesOptions"
                stacked
              ></app-input>
            </div>
          </template>

          <page-sub-footer>
            <template #before>
              <sme-alert v-if="saveError" level="danger">{{ saveError }}</sme-alert>
            </template>
            <b-button type="submit" variant="primary" :disabled="v.invalid || saving">
              <b-spinner v-if="saving" class="mr-2" small />
              {{ saving ? $t('common.addingButton') : $t('common.addButton') }}
            </b-button>
            <b-button :to="redirectUrl" variant="outline-primary"> {{ $t('admins.add.cancelButton') }} </b-button>
          </page-sub-footer>
        </b-form>
      </ValidationObserver>
    </template>

    <!-- Original User Form (when tescoPortalStubsEnabled is false) -->
    <template v-else>
      <user-form
        v-model="admin"
        :duplicates="errorDuplicated"
        :error="saveError"
        :cancel-url="redirectUrl"
        :hide-username="shouldHideUsername"
        @save="onSave"
        @duplicate-input="onDuplicateInput"
      ></user-form>
    </template>
  </div>
</template>

<script>
import { ValidationObserver } from 'vee-validate';
import uuidv4 from 'uuid/v4';
import ApiClient from '@/ApiClient';
import AppInput from '@/components/AppInput.vue';
import PageSubFooter from '@/components/PageSubFooter.vue';
import SmeAlert from '@/components/atoms/SmeAlert.vue';
import UserForm from '@/components/UserForm.vue';
import State from '@/state/State';
import useFeatureFlags from '@/composables/useFeatureFlags';
import useDivisionSelection from '@/state/composables/useDivisionSelection';

const defaultAdmin = {
  username: undefined,
  phone: null,
  email: undefined,
  password: undefined,
  confirmed: undefined,
};

export default {
  name: 'AdminAddForm',
  components: { ValidationObserver, AppInput, PageSubFooter, SmeAlert, UserForm },
  props: {
    redirectUrl: {
      type: Object,
      default: () => ({ name: 'admins' }),
    },
    hideUsername: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { tescoPortalStubsEnabled, recognitionVersion } = useFeatureFlags();
    const { divisions, hasDivisions } = useDivisionSelection();

    return {
      tescoPortalStubsEnabled,
      recognitionVersion,
      divisions,
      hasDivisions,
    };
  },
  mounted() {
    this.shouldHideUsername = State.state.isSelfServeSME;

    // Initialize Tesco-specific data only when feature is enabled
    if (this.tescoPortalStubsEnabled) {
      // Initialize all divisions as selected by default
      if (this.divisions && this.divisions.length > 0) {
        this.selectedDivisions = this.divisions.map(d => d.division_id);
      }

      // Initialize all portal functionality options as checked
      this.portalFunctionality = this.portalFunctionalityOptions.map(opt => opt.value);

      // Initialize all employee data attributes as checked
      this.employeeDataAttributes = this.employeeDataAttributesOptions.map(opt => opt.value);
    }
  },
  data() {
    return {
      error: undefined,
      saveError: undefined,
      errorDuplicated: {},
      loading: true,
      admin: { ...defaultAdmin },
      saving: false,
      success: false,
      shouldHideUsername: this.hideUsername,
      adminRole: 'company_admin',

      // Tesco-specific fields
      tescoAdminType: undefined,
      portalFunctionality: [],
      selectedDivisions: [],
      reportingLevel: 'all_levels',
      employeeDataAttributes: [],

      // Admin type options
      adminTypeOptions: [
        { value: 'super_admin', text: this.$t('admins.add.adminTypeOptions.superAdmin') },
        { value: 'admin', text: this.$t('admins.add.adminTypeOptions.admin') },
        { value: 'admin_manager', text: this.$t('admins.add.adminTypeOptions.adminManager') },
      ],

      // Portal functionality options
      portalFunctionalityOptions: [
        { value: 'dashboard', text: this.$t('nav.dashboard') },
        { value: 'analytics', text: this.$t('nav.analytics') },
        { value: 'funding_position', text: this.$t('nav.fundingPosition') },
        { value: 'divisions', text: this.$t('nav.divisions') },
        { value: 'employees', text: this.$t('nav.employees') },
        { value: 'shifts', text: this.$t('nav.shifts') },
        { value: 'absences', text: this.$t('nav.absences') },
        { value: 'pay', text: this.$t('nav.pay') },
        { value: 'admins', text: this.$t('nav.admins') },
        { value: 'integrations', text: this.$t('nav.integrations') },
        { value: 'invoices', text: this.$t('nav.invoices') },
        { value: 'settings', text: this.$t('nav.settings') },
      ],

      // Reporting level options
      reportingLevelOptions: [
        { value: 'all_levels', text: this.$t('admins.add.reportingLevelOptions.allLevels') },
        { value: 'one_level', text: this.$t('admins.add.reportingLevelOptions.oneLevel') },
        { value: 'two_levels', text: this.$t('admins.add.reportingLevelOptions.twoLevels') },
      ],
    };
  },
  computed: {
    adminRoleOptions() {
      return [
        { value: 'company_admin', text: this.$t('admins.add.adminRoleOptions.fullAccess') },
        { value: 'recognition_admin', text: this.$t('admins.add.adminRoleOptions.recognitionOnly') },
        { value: 'recognition_viewer', text: this.$t('admins.add.adminRoleOptions.recognitionViewer') },
      ];
    },
    adminRoleDescription() {
      return {
        recognition_admin: this.$t('admins.add.roleDescription'),
        recognition_viewer: this.$t('admins.add.recognitionViewerDescription'),
      }[this.adminRole];
    },
    showAdminRoleSelector() {
      return this.recognitionVersion !== 'off' && !this.tescoPortalStubsEnabled;
    },

    divisionOptions() {
      const divisionsList = this.divisions || [];
      return divisionsList.map(division => ({
        value: division.division_id,
        text:
          division.division_name ||
          division.name ||
          `${this.$t('admins.add.divisionFallbackLabel')} ${division.division_id}`,
      }));
    },

    portalFunctionalityOptionsBeforeEmployees() {
      return this.portalFunctionalityOptions.filter(opt =>
        ['dashboard', 'analytics', 'funding_position', 'divisions'].includes(opt.value),
      );
    },

    portalFunctionalityOptionsAfterEmployees() {
      return this.portalFunctionalityOptions.filter(opt =>
        ['shifts', 'absences', 'pay', 'admins', 'integrations', 'invoices', 'settings'].includes(opt.value),
      );
    },

    shouldShowEmployeeDataAttributes() {
      const requiredFunctionalities = ['funding_position', 'employees', 'shifts', 'absences', 'divisions'];
      return requiredFunctionalities.some(func => this.portalFunctionality.includes(func));
    },

    employeeDataAttributesOptions() {
      return [
        { value: 'employee_names', text: this.$t('admins.add.employeeDataAttributes.names') },
        { value: 'employee_emails', text: this.$t('admins.add.employeeDataAttributes.emails') },
        { value: 'employee_phones', text: this.$t('admins.add.employeeDataAttributes.phones') },
        {
          value: 'employee_salary_payment_type',
          text: this.$t('admins.add.employeeDataAttributes.salaryPaymentType'),
        },
        {
          value: 'employee_pay_schedule_details',
          text: this.$t('admins.add.employeeDataAttributes.payScheduleDetails'),
        },
        {
          value: 'employee_shifts',
          text: this.$t('admins.add.employeeDataAttributes.shifts'),
          disabled: this.portalFunctionality.includes('shifts'),
        },
        {
          value: 'employee_absences',
          text: this.$t('admins.add.employeeDataAttributes.absences'),
          disabled: this.portalFunctionality.includes('absences'),
        },
        { value: 'employee_pay_usage', text: this.$t('admins.add.employeeDataAttributes.payUsage') },
        { value: 'employee_status_history', text: this.$t('admins.add.employeeDataAttributes.statusHistory') },
        {
          value: 'employee_termination_information',
          text: this.$t('admins.add.employeeDataAttributes.terminationInformation'),
        },
        { value: 'employee_bank_details', text: this.$t('admins.add.employeeDataAttributes.bankDetails') },
      ];
    },
  },
  watch: {
    portalFunctionality: {
      handler(newValue) {
        // If Divisions is enabled, select all divisions and make them read-only
        const divisionsList = this.divisions || [];
        if (newValue.includes('divisions') && divisionsList.length > 0) {
          this.selectedDivisions = divisionsList.map(d => d.division_id);
        }

        // If Shifts is enabled, ensure employee_shifts is checked
        if (newValue.includes('shifts') && !this.employeeDataAttributes.includes('employee_shifts')) {
          this.employeeDataAttributes.push('employee_shifts');
        }

        // If Absences is enabled, ensure employee_absences is checked
        if (newValue.includes('absences') && !this.employeeDataAttributes.includes('employee_absences')) {
          this.employeeDataAttributes.push('employee_absences');
        }
      },
      deep: true,
    },
  },
  methods: {
    onSave() {
      this.saving = true;
      const newAdmin = {
        ...this.admin,
        user_id: uuidv4(),
      };
      if (!newAdmin.username) {
        newAdmin.username = newAdmin.email;
      }

      if (this.adminRole !== 'company_admin') {
        newAdmin.role = this.adminRole;
      }

      // Add Tesco-specific fields to the admin object if enabled
      if (this.tescoPortalStubsEnabled.value) {
        newAdmin.tesco_admin_type = this.tescoAdminType;

        if (this.tescoAdminType === 'admin') {
          newAdmin.tesco_portal_functionality = this.portalFunctionality;
          newAdmin.tesco_employee_data_attributes = this.employeeDataAttributes;

          if (this.portalFunctionality.includes('employees')) {
            newAdmin.tesco_selected_divisions = this.selectedDivisions;
            newAdmin.tesco_reporting_level = this.reportingLevel;
          }
        }
      }

      ApiClient.addAdmin(State.state.company.company_id, newAdmin).then(
        () => {
          this.$appToast(this.$t('admins.add.successToastBody', { username: newAdmin.username }), {
            title: this.$t('admins.add.successToastTitle'),
            variant: 'success',
          });
          this.$router.push(this.redirectUrl);
        },
        err => {
          this.saving = false;
          this.errorDuplicated = {
            username: false,
            phone: false,
            email: false,
          };
          if (err.message.includes('already taken')) {
            if (err.message.includes('Username')) {
              this.errorDuplicated.username = true;
            }
            if (err.message.includes('users_email_key')) {
              this.errorDuplicated.email = true;
            }
            if (err.message.includes('users_phone_key')) {
              this.errorDuplicated.phone = true;
            }
          } else {
            this.saveError = err.message;
          }
          this.$appToast(this.$t('admins.add.errorToastBody'), {
            title: this.$t('admins.add.errorToastTitle'),
            variant: 'danger',
          });
        },
      );
    },
    onDuplicateInput(fieldName) {
      this.errorDuplicated[fieldName] = false;
    },
  },
};
</script>

<style scoped>
h4 {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

h5 {
  font-size: 1.1rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
}

/* Add margin to checkbox items in the Employee Data Attributes section */
::v-deep .custom-checkbox {
  margin-bottom: 0.25rem;
}
</style>
